import { FC, useState, useEffect } from 'react'
import Image from 'next/image'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useInView } from 'react-intersection-observer'
import styles from './Pricing.module.scss'
import parse from 'html-react-parser'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { showSimulation } from 'store/simulation/actions/showSimulation'
import { trackEvent } from 'utils/eventTracking'
import { Language } from 'types/language'
import Button from 'components/button/Button'
import ButtonGroup from 'components/button-group/ButtonGroup'
import Icon from 'components/icon/Icon'
import PricingBarChart from './charts/PricingBarChart'
import { DonutChart } from './charts'
import { TITLE_POSTFIX } from 'constants/seo'
import { DonutChartFeaturedDataType as FeaturedData } from './charts/types'
import { COLORS } from './charts/constants'
import { ContractType } from './types'
import heroImage from '../../../public/images/features/pricing/header.jpg'
import pricesElectricityNLImage from '../../../public/images/features/pricing/prices-electricity-nl.png'
import pricesElectricityFRImage from '../../../public/images/features/pricing/prices-electricity-fr.png'
import pricesGasNLImage from '../../../public/images/features/pricing/prices-gas-nl.png'
import pricesGasFRImage from '../../../public/images/features/pricing/prices-gas-fr.png'
import costsElectricityNLImage from '../../../public/images/features/pricing/costs-electricity-nl.png'
import costsElectricityFRImage from '../../../public/images/features/pricing/costs-electricity-fr.png'
import costsBifuelNLImage from '../../../public/images/features/pricing/costs-bifuel-nl.png'
import costsBifuelFRImage from '../../../public/images/features/pricing/costs-bifuel-fr.png'

const Pricing: FC = () => {
  // STATE
  const [contractType, setContractType] = useState<ContractType>(ContractType.ELECTRICITY)
  const [rootMarginTop, setRootMarginTop] = useState(-200)
  const [activeFeaturedData, setActiveFeaturedData] = useState(FeaturedData.PRODUCER)

  // REDUX STORE
  const dispatch = useDispatch()

  // i18n
  const { t } = useTranslation('pricing')
  const { locale } = useRouter()

  // Bolt product prices
  const priceBolt = contractType === ContractType.ELECTRICITYGAS ? '15,98' : '9,99'
  const priceBoltOnline = contractType === ContractType.ELECTRICITYGAS ? '13,98' : '6,99'
  const priceBoltGo = contractType === ContractType.ELECTRICITYGAS ? '1,98' : '0,99'

  /**
   * Handles the click event on the CTA button
   */
  const handleCtaClick = () => {
    dispatch(showSimulation())

    // Track 'Simulation Started' event @Semetis
    trackEvent('simulation_started')
  }

  /**
   * Calculate root margin top (when exactly react-intersection-observer should be triggered)
   */
  useEffect(() => {
    const { innerHeight } = window

    setRootMarginTop(-(innerHeight / 2))
  }, [])

  // REACT-INTERSECTION-OBSERVER
  const [inViewRefProducer, inViewProducer] = useInView({ rootMargin: `0px 0px ${rootMarginTop}px 0px` })
  const [inViewRefGovernment, inViewGovernment] = useInView({ rootMargin: `0px 0px ${rootMarginTop}px 0px` })
  const [inViewRefBolt, inViewBolt] = useInView({ rootMargin: `0px 0px ${rootMarginTop}px 0px` })
  const [inViewRefGas, inViewGas] = useInView({ rootMargin: `0px 0px ${rootMarginTop}px 0px` })

  /**
   * Set active featured data (used in sticky chart) based on intersection observer's values
   */
  useEffect(() => {
    if (inViewProducer) setActiveFeaturedData(FeaturedData.PRODUCER)
    if (inViewGas) setActiveFeaturedData(FeaturedData.GAS)
    if (inViewGovernment) setActiveFeaturedData(FeaturedData.GOVERNMENT)
    if (inViewBolt) setActiveFeaturedData(FeaturedData.BOLT)
  }, [inViewProducer, inViewGas, inViewGovernment, inViewBolt])

  return (
    <div id="observer">
      {/* SEO */}
      <Head>
        <title>
          {t('seoTitle', 'Een eerlijke prijs')} {TITLE_POSTFIX}
        </title>
        <meta property="og:title" content={`${t('seoTitle')} ${TITLE_POSTFIX}`} key="title" />
      </Head>

      {/* PAGE CONTENT */}
      <section className={classNames('section', styles.section, styles.hero)}>
        <div className="container has-bg-image">
          <h1 className={styles.title}>{parse(t('title', 'Een eerlijk tarief voor jou, <span>én je energie-opwekker</span>'))}</h1>
        </div>
        <Image priority className={styles.heroImage} src={heroImage} layout="fill" placeholder="blur" alt="Bolt Energie" />
      </section>

      <section className={classNames('section', styles.section, 'is-dark', styles.description)}>
        <div className={classNames('container', styles.container)}>
          <div className={styles['description-text']}>
            {t(
              'description',
              'Bij Bolt moet je niet zijn voor een waaier aan verschillende tarieven en opties. Wij bieden een eenvoudig variabel tarief aan voor zowel energie-afnemer als -opwekker. Ons tarief geeft je inzicht in je energieprijs, zodat je zeker weet waar je stroom vandaan komt en waar je geld naartoe gaat.'
            )}
          </div>
        </div>
      </section>

      <section className={styles['energy-price-container']}>
        <div className={styles['fixed-bar']}>
          <div className={classNames('container', styles.container)}>
            <div className={styles.label}>{t('toggle.label', 'Maak je keuze:')}</div>
            <ButtonGroup>
              <button
                className={classNames('btn', {
                  active: contractType === ContractType.ELECTRICITY
                })}
                onClick={() => setContractType(ContractType.ELECTRICITY)}
              >
                {t('toggle.electricity.only', 'Enkel elektriciteit')}
              </button>
              <button
                className={classNames('btn', {
                  active: contractType === ContractType.ELECTRICITYGAS
                })}
                onClick={() => setContractType(ContractType.ELECTRICITYGAS)}
              >
                {t('toggle.electricity.gas', 'Elektriciteit en gas')}
              </button>
            </ButtonGroup>
          </div>
        </div>

        <div className={classNames('section', styles.overview)}>
          <div className={classNames('container', styles.container)}>
            <div>
              <h2>{t('energy-price.title', 'Hoe is jouw energieprijs opgebouwd?')}</h2>
              <p>
                {contractType === ContractType.ELECTRICITYGAS
                  ? t(
                      'energy-price.description.electricity.gas',
                      'Je kan je energieprijs opdelen in vier verschillende delen: de prijs voor stroom voor je energie-opwekker, de prijs voor gas, een platformkost voor Bolt en netkosten en bijdragen voor de overheid en netbeheerder. Gemiddeld gezien bestaat de som, die je voor je energie betaalt, dus slechts uit 35% van je totale energieprijs. Al deze kosten worden meegenomen in de berekening van je maandelijks voorschot. We leggen elk deel afzonderlijk voor je uit.'
                    )
                  : t(
                      'energy-price.description.electricity.only',
                      'Je kan je energieprijs opdelen in drie verschillende delen: de prijs voor stroom voor je energie-opwekker, een platformkost voor Bolt en netkosten en bijdragen voor de overheid en netbeheerder. Gemiddeld gezien bestaat de som, die je voor je energie betaalt, dus slechts uit 35% van je totale energieprijs. Al deze kosten worden meegenomen in de berekening van je maandelijks voorschot. We leggen elk deel afzonderlijk voor je uit.'
                    )}
              </p>
            </div>
            <DonutChart contractType={contractType} responsiveLabels />
          </div>
        </div>

        <div className={classNames('section', styles.story)}>
          <div className={classNames('container', styles.container)}>
            <div className={styles['chart-container']}>
              <DonutChart contractType={contractType} featuredData={activeFeaturedData} />
            </div>
            <div className={styles.content}>
              <div className={styles['data-info-block']} ref={inViewRefProducer}>
                <h3>
                  <div className={styles.dot} style={{ backgroundColor: COLORS.green }} />
                  {t('energy-price.producer.title', 'De ‘prijs van de dag’ voor jouw energie-opwekker')}
                </h3>
                <div className={styles['text-container']}>
                  <div className={styles['chart-container-mobile']}>
                    <DonutChart contractType={contractType} featuredData={FeaturedData.PRODUCER} noLabels />
                  </div>
                  <p>
                    {t(
                      'energy-price.producer.description',
                      'Bolt spant geen kabel tussen jouw woning en de installatie van jouw energie-opwekker. Je koopt je stroom lokaal doordat wij stroom voor jou gaan aankopen bij jouw gekozen opwekker. De prijs, die je voor je stroom betaalt, volgt de ups en downs van de markt. Je koopt de energie bij je lokale energie-opwekker dus aan ‘de prijs van de dag’. Op die manier kunnen wij niet alleen vandaag, maar ook in de toekomst een eerlijke prijs garanderen voor zowel energie-afnemer als opwekker.'
                    )}
                  </p>
                  <div className={styles['info-block']}>
                    <div className={styles.icon}>
                      <Icon name="info" color="currentColor" />
                    </div>
                    {parse(
                      t(
                        'energy-price.producer.crisis',
                        '<i>Dit jaar zijn de energieprijzen uitzonderlijk hoog. Dit komt door de grote vraag naar gas en elektriciteit in de nasleep van de coronacrisis. Deze situatie zal zich opnieuw normaliseren.</i>'
                      )
                    )}
                  </div>
                </div>
                <div className={styles.card}>
                  <Image
                    src={locale === Language.DUTCH ? pricesElectricityNLImage : pricesElectricityFRImage}
                    quality={100}
                    width={1522}
                    height={848}
                  />
                </div>
              </div>
              {contractType === ContractType.ELECTRICITYGAS && (
                <div className={styles['data-info-block']} ref={inViewRefGas}>
                  <h3>
                    <div className={styles.dot} style={{ backgroundColor: COLORS.purple }} />
                    {t('energy-price.gas.title', 'Gas')}
                  </h3>
                  <div className={styles['text-container']}>
                    <div className={styles['chart-container-mobile']}>
                      <DonutChart contractType={contractType} featuredData={FeaturedData.GAS} noLabels />
                    </div>
                    <p>
                      {t(
                        'energy-price.gas.description',
                        'Gas is niet hernieuwbaar. Bolt biedt ook gas aan uit praktische overwegingen voor onze energie-afnemers, maar steunt de transitie naar verwarmen op groene elektriciteit. Deze prijs is afhankelijk van je verbruik.'
                      )}
                    </p>
                    <div className={styles['info-block']}>
                      <div className={styles.icon}>
                        <Icon name="info" color="currentColor" />
                      </div>
                      {parse(
                        t(
                          'energy-price.gas.crisis',
                          '<i>Dit jaar zijn de energieprijzen uitzonderlijk hoog. Dit komt door de grote vraag naar gas en elektriciteit in de nasleep van de coronacrisis. Deze situatie zal zich opnieuw normaliseren.</i>'
                        )
                      )}
                    </div>
                  </div>
                  <div className={styles.card}>
                    <Image src={locale === Language.DUTCH ? pricesGasNLImage : pricesGasFRImage} quality={100} width={1522} height={848} />
                  </div>
                </div>
              )}
              <div className={styles['data-info-block']} ref={inViewRefGovernment}>
                <h3>
                  <div className={styles.dot} style={{ backgroundColor: COLORS.darkBlue }} />
                  {t('energy-price.government-operator.title', 'Netkosten en bijdragen')}
                </h3>
                <div className={styles['text-container']}>
                  <div className={styles['chart-container-mobile']}>
                    <DonutChart contractType={contractType} featuredData={FeaturedData.GOVERNMENT} noLabels />
                  </div>
                  <p>
                    {t(
                      'energy-price.government-operator.description',
                      'Het grootste deel van je geld gaat naar de overheid en netbeheerder. Langs de ene kant betaal je netkosten aan de netbeheerder voor de goede werking van het Belgisch stroomnet en het transport van energie tot bij je thuis, langs de andere kant vraagt de overheid tal van bijdragen om het energiebeleid in ons land verder uit te bouwen. Deze kosten zijn bij alle energieleveranciers gelijk en worden door de leverancier rechtstreeks doorgestort naar de overheid en netbeheerder.'
                    )}
                  </p>
                </div>
                <div className={styles.card}>
                  <Image
                    src={
                      contractType === ContractType.ELECTRICITYGAS
                        ? locale === Language.DUTCH
                          ? costsBifuelNLImage
                          : costsBifuelFRImage
                        : locale === Language.DUTCH
                        ? costsElectricityNLImage
                        : costsElectricityFRImage
                    }
                    width={1448}
                    height={664}
                    quality={100}
                  />
                </div>
              </div>
              <div className={styles['data-info-block']} ref={inViewRefBolt}>
                <h3>
                  <div className={styles.dot} style={{ backgroundColor: COLORS.yellow }} />
                  {t('energy-price.bolt.title', 'Een maandelijkse platformkost')}
                </h3>
                <div className={styles['text-container']}>
                  <div className={styles['chart-container-mobile']}>
                    <DonutChart contractType={contractType} featuredData={FeaturedData.BOLT} noLabels />
                  </div>
                  <p>
                    {t(
                      'energy-price.bolt.description',
                      'Aan Bolt betaal je enkel een vaste maandelijkse platformkost. We nemen dus geen marge op de energie, die je verbruikt. Deze kost verandert nooit en vragen we voor onze diensten en het optimaliseren van ons platform. We hebben 3 verschillende formules:'
                    )}
                  </p>
                </div>
                <div className={classNames(styles.card, styles.product)}>
                  <div>
                    <h4>Bolt</h4>
                    <div className={styles.price}>
                      €{priceBolt} /{t('month', 'maand')}
                    </div>
                  </div>
                  <ul>
                    <li>
                      <Icon name="check" color={COLORS.yellow} />
                      <span>{t('product.usp.local-energy', 'Lokale, duurzame stroom')}</span>
                    </li>
                    <li>
                      <Icon name="check" color={COLORS.yellow} />
                      <span>
                        {parse(t('product.usp.montly-deposit-yearly-settlement', 'Maandelijks voorschot,<br />jaarlijkse afrekening'))}
                      </span>
                    </li>
                    <li>
                      <Icon name="check" color={COLORS.yellow} />
                      <span>{t('product.usp.invoice.paper', 'Jouw factuur op papier')}</span>
                    </li>
                    <li>
                      <Icon name="check" color={COLORS.yellow} />
                      <span>{t('product.usp.customer-portal', 'Online klantenportaal')}</span>
                    </li>
                  </ul>
                </div>
                <div className={classNames(styles.card, styles.product)}>
                  <div>
                    <h4>Bolt Online</h4>
                    <div className={styles.price}>
                      €{priceBoltOnline} /{t('month', 'maand')}
                    </div>
                  </div>
                  <ul>
                    <li>
                      <Icon name="check" color={COLORS.yellow} />
                      <span>{t('product.usp.local-energy', 'Lokale, duurzame stroom')}</span>
                    </li>
                    <li>
                      <Icon name="check" color={COLORS.yellow} />
                      <span>
                        {parse(t('product.usp.montly-deposit-yearly-settlement', 'Maandelijks voorschot,<br />jaarlijkse afrekening'))}
                      </span>
                    </li>
                    <li>
                      <Icon name="check" color={COLORS.yellow} />
                      <span>{t('product.usp.invoice.email', 'Jouw factuur via e-mail')}</span>
                    </li>
                    <li>
                      <Icon name="check" color={COLORS.yellow} />
                      <span>{t('product.usp.customer-portal', 'Online klantenportaal')}</span>
                    </li>
                  </ul>
                </div>
                <div className={classNames(styles.card, styles.product)}>
                  <div>
                    <h4>Bolt Go</h4>
                    <div className={styles.price}>
                      €{priceBoltGo} /{t('month', 'maand')}
                    </div>
                    <div className={styles['button-container']}>
                      <a href="https://go.boltenergie.be/" target="_blank" rel="noopener noreferrer" className="btn with-gradient">
                        {t('product.button', 'Meer info')}
                      </a>
                    </div>
                  </div>
                  <ul>
                    <li>
                      <Icon name="check" color={COLORS.yellow} />
                      <span>{t('product.usp.local-energy', 'Lokale, duurzame stroom')}</span>
                    </li>
                    <li>
                      <Icon name="check" color={COLORS.yellow} />
                      <span>
                        {parse(t('product.usp.one-off-deposit-yearly-settlement', 'Eenmalig voorschot,<br />jaarlijkse afrekening'))}
                      </span>
                    </li>
                    <li>
                      <Icon name="check" color={COLORS.yellow} />
                      <span>{t('product.usp.invoice.email', 'Jouw factuur via e-mail')}</span>
                    </li>
                    <li>
                      <Icon name="check" color={COLORS.yellow} />
                      <span>{t('product.usp.customer-portal', 'Online klantenportaal')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={classNames('section', styles.section, 'is-dark', styles.chart)}>
        <div className={classNames('container', styles.container)}>
          <h2>{parse(t('chartTopTitle', 'Goed voor de planeet <span>én je portefeuille</span>'))}</h2>
          <p>
            {t(
              'chartTopDescription',
              'Bij Bolt moet je niet zijn voor complexe prijsformules. Wij bieden slechts 1 variabele prijs aan. Eentje die de échte prijs van energie weerspiegelt en dus de marktprijs volgt. Bovendien sjoemelen wij niet met ons tarief, zoals de meeste andere leveranciers, en verhogen wij nooit je prijs achter je rug. Daardoor kom je nooit voor verrassingen te staan en zit je ook goed bij Bolt op lange termijn, voor je portefeuille én de planeet.'
            )}
          </p>
          <PricingBarChart />
        </div>
      </section>

      <section className={classNames('section', styles.section, styles.cta)}>
        <div className={classNames('container', styles.container)}>
          <div className={styles.content}>
            <h2>{parse(t('ctaTitle', 'Stap over in slechts 5 minuten.<br /><span>Wij regelen alles!</span>'))}</h2>
            <Button onClick={() => handleCtaClick()} isFullWidthMobile withGradient>
              {t('ctaButton', 'Bereken jouw prijs')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Pricing
